/**
 * Typed API client.
 *
 * Reads the ONE shared envelope (`{ ok, data, meta }` / `{ ok, error }`), which
 * is the whole point of normalising the contract: the previous admin had to
 * special-case three different response shapes, and had to remember that some
 * endpoints used `error` while others used `message`, and that checkout returned
 * `order.number` where every other order surface used `order.order_number`.
 */
import type { ApiResponse, PageMeta, ApiErrorCode } from "@clubghor/types";

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly fields?: Record<string, string>;

  constructor(status: number, code: ApiErrorCode, message: string, fields?: Record<string, string>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    if (fields) this.fields = fields;
  }

  /** True when the session is gone, so the UI can bounce to sign-in. */
  get isAuthError(): boolean {
    return this.status === 401;
  }

  get fieldFor(): (field: string) => string | undefined {
    return (field: string) => this.fields?.[field];
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  /** Query params; null/undefined/"" entries are dropped. */
  params?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  /** Set for FormData uploads, where the browser must set the boundary itself. */
  rawBody?: BodyInit;
  signal?: AbortSignal;
}

export interface Page<T> {
  rows: T[];
  meta: PageMeta;
}

function buildUrl(path: string, params?: RequestOptions["params"]): string {
  const url = new URL(path.startsWith("/") ? path : `/${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.pathname + url.search;
}

/**
 * Issues a request and unwraps the envelope.
 * Throws ApiError for every failure, so callers never branch on `res.ok`.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", params, body, rawBody, signal } = options;

  const headers = new Headers({ Accept: "application/json" });
  let payload: BodyInit | undefined = rawBody;

  if (body !== undefined && rawBody === undefined) {
    headers.set("Content-Type", "application/json");
    payload = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, params), {
      method,
      headers,
      body: payload,
      // Same-origin API, so the session cookie rides along on its own.
      credentials: "same-origin",
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    // A network failure is not an API error; give it a code the UI understands.
    throw new ApiError(0, "internal_error", "Could not reach the server. Check your connection.");
  }

  let parsed: ApiResponse<T> | null = null;
  try {
    parsed = (await response.json()) as ApiResponse<T>;
  } catch {
    // Non-JSON response: a proxy error page, or a 204. Fall through to the
    // status-based error below rather than throwing a parse error at the caller.
  }

  if (parsed && parsed.ok) return parsed.data;

  if (parsed && !parsed.ok) {
    throw new ApiError(response.status, parsed.error.code, parsed.error.message, parsed.error.fields);
  }

  // A 2xx with an empty or non-JSON body (a 204, say) is a success, not a
  // failure: falling through used to turn it into a phantom "Request failed".
  if (response.ok) return undefined as T;

  throw new ApiError(response.status, "internal_error", `Request failed (${response.status}).`);
}

/** Same as request(), but also returns pagination meta. */
export async function requestPage<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ rows: T[]; meta: PageMeta }> {
  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.params), {
      method: options.method ?? "GET",
      headers: options.body
        ? { "Content-Type": "application/json", Accept: "application/json" }
        : { Accept: "application/json" },
      body: options.body ? JSON.stringify(options.body) : undefined,
      credentials: "same-origin",
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    // Same message as request(): a raw "Failed to fetch" TypeError used to reach
    // every list page's error state verbatim.
    throw new ApiError(0, "internal_error", "Could not reach the server. Check your connection.");
  }

  const parsed = (await response.json().catch(() => null)) as
    | { ok: true; data: T[]; meta: PageMeta }
    | { ok: false; error: { code: ApiErrorCode; message: string; fields?: Record<string, string> } }
    | null;

  if (parsed?.ok) {
    return { rows: parsed.data, meta: parsed.meta };
  }
  if (parsed && !parsed.ok) {
    throw new ApiError(response.status, parsed.error.code, parsed.error.message, parsed.error.fields);
  }
  if (response.ok) {
    return { rows: [] as T[], meta: { total: 0, page: 1, perPage: 0, pages: 1 } };
  }
  throw new ApiError(response.status, "internal_error", `Request failed (${response.status}).`);
}

export const api = {
  get: <T>(path: string, params?: RequestOptions["params"], signal?: AbortSignal) =>
    request<T>(path, { method: "GET", params, signal }),
  list: <T>(path: string, params?: RequestOptions["params"], signal?: AbortSignal) =>
    requestPage<T>(path, { method: "GET", params, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", rawBody: form }),
};

/** Human message for a mutation error, preferring the field map when present. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}
